import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useJuniorAliveNav } from '../../contexts/JuniorAliveNavContext';
import {
  buildPlaybackEvents,
  clampMeasureCount,
  clearNoteRefs,
  createDefaultSessionConfig,
  emptyGrid,
  filterGridForKey,
  isGridNonEmpty,
  playbackDurationMs,
  resizeGridForTimeSignature,
  internalStepDurationMs,
  toggleVisualCell,
  totalInternalSteps,
  type GridNoteRef,
  type MelodiaSessionConfig,
  type MelodyPitchId,
} from '../../lib/melody/melodiaBuilderConfig';
import type { BuildTool, ChordDegreeId, MelodyKeyId, Subdivision } from '../../lib/melody/melodiaKeys';
import { pitchesForKey } from '../../lib/melody/melodiaKeys';
import { playMelodyNote, playMelodyPreview, resumeMelodyAudio } from '../../lib/melody/melodyNoteSounds';
import type { TimeSignature } from '../../lib/rhythm/ecoRitmoConfig';
import { MelodiaConstructPanel } from './MelodiaConstructPanel';
import { MelodiaGlobalSettings } from './MelodiaGlobalSettings';

const MAX_UNDO = 40;

function refsFromSelection(grid: MelodiaSessionConfig['grid'], keys: Set<string>): GridNoteRef[] {
  return [...keys].map((key) => {
    const [mi, si] = key.split(':').map(Number);
    return { measureIndex: mi!, internalSlot: si! };
  });
}

export const MelodiaBuilder: React.FC = () => {
  const { navigateAlive } = useJuniorAliveNav();
  const [config, setConfig] = useState<MelodiaSessionConfig>(createDefaultSessionConfig);
  const [selectedPitch, setSelectedPitch] = useState<MelodyPitchId>('Sol4');
  const [tool, setTool] = useState<BuildTool>('pencil');
  const [activeChord, setActiveChord] = useState<ChordDegreeId | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);

  const undoStack = useRef<MelodiaSessionConfig[]>([]);
  const [undoCount, setUndoCount] = useState(0);
  const rafRef = useRef<number | null>(null);
  const playedRef = useRef<Set<number>>(new Set());
  const playStartRef = useRef(0);

  const canPlay = isGridNonEmpty(config.grid);
  const canUndo = undoCount > 0;

  const pushUndo = useCallback((prev: MelodiaSessionConfig) => {
    undoStack.current = [...undoStack.current.slice(-(MAX_UNDO - 1)), prev];
    setUndoCount(undoStack.current.length);
  }, []);

  const applyConfig = useCallback(
    (updater: (prev: MelodiaSessionConfig) => MelodiaSessionConfig) => {
      setConfig((prev) => {
        const next = updater(prev);
        if (next !== prev) pushUndo(prev);
        return next;
      });
    },
    [pushUndo]
  );

  const ensurePitchInKey = useCallback((keyId: MelodyKeyId, pitch: MelodyPitchId): MelodyPitchId => {
    const allowed = pitchesForKey(keyId);
    if (allowed.includes(pitch)) return pitch;
    return allowed[0] ?? 'Do4';
  }, []);

  const stopPlayback = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    playedRef.current = new Set();
    setIsPlaying(false);
    setActiveStep(-1);
  }, []);

  useEffect(() => () => stopPlayback(), [stopPlayback]);

  const handleUndo = useCallback(() => {
    const stack = undoStack.current;
    if (!stack.length) return;
    const prev = stack[stack.length - 1]!;
    undoStack.current = stack.slice(0, -1);
    setUndoCount(undoStack.current.length);
    setConfig(prev);
    setSelectedPitch(ensurePitchInKey(prev.keyId, selectedPitch));
    setSelection(new Set());
  }, [ensurePitchInKey, selectedPitch]);

  const deleteSelection = useCallback(() => {
    if (!selection.size) return;
    const refs = refsFromSelection(config.grid, selection);
    applyConfig((prev) => ({
      ...prev,
      grid: clearNoteRefs(prev.grid, refs),
    }));
    setSelection(new Set());
  }, [applyConfig, config.grid, selection]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isPlaying || !selection.size) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelection();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteSelection, isPlaying, selection.size]);

  const handleCellClick = useCallback(
    (measureIndex: number, visualSlot: number, pitch: MelodyPitchId) => {
      setSelectedPitch(pitch);
      playMelodyPreview(pitch, config.soundId);
      applyConfig((prev) => ({
        ...prev,
        grid: toggleVisualCell(prev.grid, measureIndex, visualSlot, prev.subdivision, pitch),
      }));
      setSelection(new Set());
    },
    [applyConfig, config.soundId]
  );

  const handleMeasureCountChange = (count: number) => {
    const next = clampMeasureCount(count);
    applyConfig((prev) => {
      let grid = prev.grid;
      if (next > prev.measureCount) {
        grid = [...grid, ...emptyGrid(next - prev.measureCount, prev.timeSignature)];
      } else {
        grid = grid.slice(0, next);
      }
      return { ...prev, measureCount: next, grid };
    });
  };

  const handleTimeSignature = (timeSignature: TimeSignature) => {
    applyConfig((prev) => ({
      ...prev,
      timeSignature,
      grid: resizeGridForTimeSignature(prev.grid, timeSignature),
    }));
  };

  const handleKeyChange = (keyId: MelodyKeyId) => {
    applyConfig((prev) => ({
      ...prev,
      keyId,
      grid: filterGridForKey(prev.grid, keyId),
    }));
    setSelectedPitch((p) => ensurePitchInKey(keyId, p));
    setActiveChord(null);
    setSelection(new Set());
  };

  const handleSubdivisionChange = (subdivision: Subdivision) => {
    setConfig((prev) => ({ ...prev, subdivision }));
  };

  const handleToolChange = (next: BuildTool) => {
    setTool(next);
    if (next === 'pencil') setSelection(new Set());
  };

  const startPlayback = useCallback(async () => {
    await resumeMelodyAudio();
    const events = buildPlaybackEvents(config);
    if (!events.length) return;

    stopPlayback();
    setIsPlaying(true);
    setSelection(new Set());
    playStartRef.current = performance.now();
    playedRef.current = new Set();

    const stepMs = internalStepDurationMs(config);
    const maxStep = totalInternalSteps(config) - 1;

    const loop = () => {
      const elapsed = performance.now() - playStartRef.current;

      for (const ev of events) {
        if (!playedRef.current.has(ev.step) && elapsed >= ev.timeMs) {
          playedRef.current.add(ev.step);
          playMelodyNote(ev.pitchId, config.soundId, 0.01);
        }
      }

      setActiveStep(Math.min(Math.floor(elapsed / stepMs), maxStep));

      if (elapsed >= playbackDurationMs(config)) {
        stopPlayback();
        return;
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
  }, [config, stopPlayback]);

  return (
    <div className="melodia-builder flex flex-col flex-1 min-h-0 overflow-hidden">
      <header className="melodia-builder-header shrink-0 flex items-center gap-2 px-2 py-1">
        <button
          type="button"
          onClick={() => navigateAlive('/melodia')}
          className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-lg"
          aria-label="Volver a Melodía"
        >
          ←
        </button>

        <div className="melodia-title-sign flex-1 flex justify-center min-w-0">
          <h1 className="melodia-builder-title">Constructor de melodías</h1>
        </div>

        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="w-9 h-9 rounded-full text-sm shrink-0 font-medium"
          aria-label="Ayuda"
        >
          ?
        </button>
      </header>

      {showHelp && (
        <div className="melodia-help shrink-0 mx-2 px-3 py-1.5 rounded-xl text-[11px] leading-snug">
          Toca un carril para colocar o quitar una nota. Usa el lazo para seleccionar varias y
          bórralas con Supr. Cambiar el detalle de grilla solo ajusta la vista — tus notas se
          mantienen. PLAY reproduce la melodía.
        </div>
      )}

      <div className="melodia-builder-body flex-1 min-h-0 overflow-hidden px-2 pb-1">
        <MelodiaConstructPanel
          config={config}
          activeStep={activeStep}
          isPlaying={isPlaying}
          tool={tool}
          activeChord={activeChord}
          selection={selection}
          selectedPitch={selectedPitch}
          canUndo={canUndo}
          onTool={handleToolChange}
          onUndo={handleUndo}
          onClearSelection={() => setSelection(new Set())}
          onKeyChange={handleKeyChange}
          onSubdivisionChange={handleSubdivisionChange}
          onChordSelect={setActiveChord}
          onSelectionChange={setSelection}
          onSelectPitch={setSelectedPitch}
          onCellClick={handleCellClick}
          onMeasureCountChange={handleMeasureCountChange}
        />
      </div>

      <MelodiaGlobalSettings
        config={config}
        onTimeSignature={handleTimeSignature}
        onBpm={(bpm) => setConfig((prev) => ({ ...prev, bpm }))}
        onSound={(soundId) => setConfig((prev) => ({ ...prev, soundId }))}
        canPlay={canPlay}
        isPlaying={isPlaying}
        onPlay={startPlayback}
        onStop={stopPlayback}
      />
    </div>
  );
};

export default MelodiaBuilder;
